import * as React from 'react'

import { Image } from '@thg-commerce/gravity-elements'
import {
  spacing,
  styled,
  TextEntry,
  TextStyle,
} from '@thg-commerce/gravity-theme'
import { Padding } from '@thg-commerce/gravity-theme/padding'

import { Controls } from '../ImageGallery/Controls'
import { ImageGalleryContextProvider } from '../ImageGallery/ImageGalleryContextProvider'
import { Position } from '../ImageGallery/types'
import VideoPlayer from '../ImageGallery/VideoTranscript/VideoSlide'
import { VideoTranscriptModal } from '../ImageGallery/VideoTranscript/VideoTranscriptModal'

interface I18nText {
  closeAriaLabel: string
  closeLabel: string
}
export interface ImageGridProps {
  gapSpacing: number
  columnCount: number
  images: {
    url: string
    alt: string
    tag?: string
    isVideoUrl?: boolean
    videoTitle?: string
    videoTranscript?: string
  }[]
  className?: string
  imageAspectRatio?: string
  zoomEnabled?: boolean
  desktopZoomEnabled?: boolean
  i18nText: {
    zoomOutLabel: string
    zoomInLabel: string
    closeI18nText: I18nText
  }
  zoomOnClickHandler?: (imageIndex: number) => void
  controlIcons?: {
    zoomIn?: string
    fill?: string
  }
  tagStyle?: {
    container: {
      padding: Padding
    }
    textStyling: {
      entry: TextEntry
      style: TextStyle
    }
  }
}

const Grid = styled.div<Pick<ImageGridProps, 'gapSpacing' | 'columnCount'>>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columnCount}, 1fr);
  gap: ${(props) => spacing(props.gapSpacing)};
`

const ImageWrapper = styled.div<{ zoomClickable?: boolean }>`
  cursor: ${(props) => props.zoomClickable && 'zoom-in'};
`

const IMAGES_INITIALLY_VISIBLE = 4

const getTranslatedModalText = (title: string, i18n: I18nText) => ({
  title,
  closeI18nText: {
    closeAriaLabel: i18n.closeAriaLabel,
    closeLabel: i18n.closeLabel,
  },
})

export const ImageGrid: React.FunctionComponent<ImageGridProps> = (props) => {
  const [isTranscriptOpen, setIsTranscriptOpen] = React.useState(false)
  const [activeTranscript, setActiveTranscript] = React.useState<string>()
  const [modalTitle, setModalTitle] = React.useState('')

  const ZoomControlProps = {
    controlSize: [48],
    i18nText: {
      zoomInLabel: props.i18nText.zoomInLabel,
      zoomOutLabel: props.i18nText.zoomOutLabel,
    },
    showCarouselControls: [false],
    zoomControlPosition: [Position.TOP_MIDDLE],
    zoomOpen: false,
    controlIcons: props.controlIcons,
  }

  return (
    <ImageGalleryContextProvider
      zoomButtonClickHandler={props.zoomOnClickHandler}
    >
      <Grid
        className={props.className}
        columnCount={props.columnCount}
        gapSpacing={props.gapSpacing}
        data-testid="image-grid"
      >
        {props.zoomEnabled && (
          <Controls
            {...ZoomControlProps}
            selectedImageIndex={0}
            location={Position.TOP_MIDDLE}
            desktopZoomEnabled={props.desktopZoomEnabled}
          />
        )}
        {props.images.map((image, index) => (
          <ImageWrapper
            zoomClickable={props.desktopZoomEnabled}
            onClick={() =>
              props.desktopZoomEnabled
                ? props.zoomOnClickHandler && props.zoomOnClickHandler(index)
                : undefined
            }
          >
            {image.isVideoUrl ? (
              <VideoPlayer
                url={image}
                onTranscriptClick={() => {
                  setActiveTranscript(image.videoTranscript)
                  setModalTitle(image.videoTitle || '')
                  setIsTranscriptOpen(true)
                }}
                transcriptionButtonAlignment={{
                  left: '130px',
                  bottom: '36px',
                }}
              />
            ) : (
              <Image
                testId={`image-grid-image-${index}`}
                src={[{ url: image.url }]}
                alt={image.alt}
                tag={image.tag}
                width="100%"
                aspectRatio={props.imageAspectRatio || '1/1'}
                lazy={index > IMAGES_INITIALLY_VISIBLE - 1}
                tagStyle={props.tagStyle}
              />
            )}
          </ImageWrapper>
        ))}
        {isTranscriptOpen && (
          <VideoTranscriptModal
            i18nText={getTranslatedModalText(
              modalTitle,
              props.i18nText.closeI18nText,
            )}
            content={activeTranscript}
            onClose={() => setIsTranscriptOpen(false)}
          />
        )}
      </Grid>
    </ImageGalleryContextProvider>
  )
}
